import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // For the "not in kofschip" indicator shown below the grid
  const notInKofschipIndicator = !isInKofschip ? <div className="flex items-center justify-center gap-2 mt-2" data-id="element-13541" data-mp="1gxeje0iaxf1m">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-sm" data-id="element-13542" data-mp="1gxxt4txssx4k">
        {lastLetter.toUpperCase()}
      </div>
      <span className="text-sm font-semibold text-rose-600" data-id="element-13543" data-mp="171kx3pes676n">
        ← niet in 't kofschip
      </span>
    </div> : null;
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-13544" data-mp="1qt9lma99nojv">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-13545" data-mp="1qukqkdeg72ck">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid with color coding */}
        <div className="flex items-center gap-1.5" data-id="element-13546" data-mp="1h3pz8umchck9">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-13547" data-mp="24ssha3kw57fa">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-13548" data-mp="1vrulu9oiq88b">
                
                {letter}
              </div>;
        })}
        </div>
        {/* Show the tested letter in red if NOT in kofschip */}
        {notInKofschipIndicator}
        {/*
               NOTE (antwoord-lek fix): the previous "stem + -te/-de" result badge
               was REMOVED from stimulus mode. As a stimulus it pre-computed the
               full kofschip decision AND revealed the intact loanword stem-spelling
               — i.e. it handed the learner the exact answer the MC/fill-in item is
               meant to test (e.g. seed stem:"race", useTE:true → showed "race + -te"
               → option "racete"). The KOFSCHIP letter grid + "niet in 't kofschip"
               indicator stay: they present the *mnemonic tool* (and, for klank-vs-
               letter traps like 'race', the deliberate misconception) without
               resolving the answer. The full worked result still lives in SUPPORT
               mode below, shown via the rule/support aid, not in the stimulus.
             */}
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13549" data-mp="335yr2bg8og">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-13550" data-mp="2bm2di2zze0">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13551" data-mp="8vez4kotea84">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-13552" data-mp="8vopi9drujec">
            <span className="text-2xl" data-id="element-13553" data-mp="dwcbuhr22c28">⛵</span>
            <div className="text-left" data-id="element-13554" data-mp="3da50kf8x9tp0">
              <div className="text-sm font-bold text-blue-900" data-id="element-13555" data-mp="3db6a2faai84m">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-13556" data-mp="3ma1ky4oa46e9">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Ezelsbruggetje (Tip) */}
        <div className="mb-6 bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13557" data-mp="11kc3rl03s8ie">
          <div className="flex items-start gap-3" data-id="element-13558" data-mp="16inerz24wp27">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13559" data-mp="16h2io5068y28">
              <span className="text-amber-600 text-lg" data-id="element-13560" data-mp="2fhyazk177j06">💡</span>
            </div>
            <div data-id="element-13561" data-mp="2aibzbf10qath">
              <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13562" data-mp="2aiu4j15nrzdj">
                Ezelsbruggetje
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13563" data-mp="2jhfj6l80eu91">
                Zit de laatste letter van de stam in{' '}
                <strong data-id="element-13564" data-mp="2ydwq90961mps">'t KOFSCHIP</strong>?
                <br data-id="element-13565" data-mp="2yclof5za6p1b" />✅ Ja → <strong data-id="element-13566" data-mp="2ycdadarf9doj">-te(n)</strong>
                <br data-id="element-13567" data-mp="2tget074igvxm" />❌ Nee → <strong data-id="element-13568" data-mp="2tek8uv12d7ul">-de(n)</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Stap-voor-stap voorbeeld (Compact) */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-13569" data-mp="335etjuynvzdx">
          <div className="flex items-start gap-3" data-id="element-13570" data-mp="337so82pfj9ky">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13571" data-mp="3h5zcqs8hnx7y">
              <span className="text-emerald-600 text-lg" data-id="element-13572" data-mp="3c7o4un1r7wsd">📝</span>
            </div>
            <div className="w-full" data-id="element-13573" data-mp="3vsxz48sho1ft">
              <h4 className="text-sm font-bold text-emerald-900 mb-4" data-id="element-13574" data-mp="3vptsz5vgqvmi">
                Voorbeeld: {verb}
              </h4>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-13575" data-mp="3ltz1dl99hq7l">
                <div className="text-center" data-id="element-13576" data-mp="3qr8ogu96hps9">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-13577" data-mp="3d1eww46agreu">Stam</div>
                  <div className="font-bold text-lg text-blue-700" data-id="element-13578" data-mp="3czbz2xyw9tzk">{stem}</div>
                </div>
                <div className="text-slate-400" data-id="element-13579" data-mp="3hu83kxlbi9tk">→</div>
                <div className="text-center" data-id="element-13580" data-mp="3340mal71qwug">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-13581" data-mp="3314nyvrae65h">
                    Laatste letter
                  </div>
                  <div className={`font-bold text-xl ${isInKofschip ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-13582" data-mp="2u2sxquuc1adk">
                    
                    {lastLetter.toUpperCase()}
                  </div>
                </div>
                <div className="text-slate-400" data-id="element-13583" data-mp="14loyd2261f80">→</div>
                <div className="text-center" data-id="element-13584" data-mp="14n8ij3qy0af2">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-13585" data-mp="ztnspyzi6mqy">
                    In 't KOFSCHIP?
                  </div>
                  <div className="font-bold text-lg" data-id="element-13586" data-mp="uw5f3vajjjcm">
                    {isInKofschip ? '✅ Ja' : '❌ Nee'}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center p-3 rounded-lg border-2 font-bold bg-white shadow-sm" data-id="element-13587" data-mp="1nkjdic5jkhop">
                Dus: <span className="text-emerald-700" data-id="element-13588" data-mp="1im83a0bc455y">{stem}</span> +{' '}
                <span className="text-emerald-700" data-id="element-13589" data-mp="1dlkhbt1yqeil">
                  {useTE ? 'te(n)' : 'de(n)'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Detailed Steps */}
        <details className="group bg-white/60 rounded-xl border border-blue-100 shadow-sm overflow-hidden" data-id="element-13590" data-mp="27q3zjuj5l6k">
          <summary className="cursor-pointer p-4 font-semibold text-blue-800 hover:bg-blue-50/50 transition-colors flex items-center justify-between" data-id="element-13591" data-mp="3u965493ikn4w">
            <span data-id="element-13592" data-mp="2du0o2hy6swrb">Meer uitleg (stap voor stap)</span>
            <span className="text-blue-400 group-open:rotate-180 transition-transform" data-id="element-13593" data-mp="2dutjwnvly1uo">
              ▼
            </span>
          </summary>
          <div className="p-5 border-t border-blue-100 space-y-6" data-id="element-13594" data-mp="2njtn6jh57jeo">
            {/* Step 1: Verb → Stem */}
            <div className="flex items-start gap-3" data-id="element-13595" data-mp="2sjg9diey89zm">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-13596" data-mp="2siwvccsf65qv">
                <span className="text-blue-600 font-bold text-xs" data-id="element-13597" data-mp="2xk43we2dz8yl">1</span>
              </div>
              <div data-id="element-13598" data-mp="31hs3huf0xpwt">
                <h4 className="text-sm font-bold text-blue-900 mb-1" data-id="element-13599" data-mp="36ipgztp39vrz">
                  Vind de stam
                </h4>
                <p className="text-sm text-slate-600" data-id="element-13600" data-mp="176n26wretpkn">
                  Haal -en van het hele werkwoord af: <strong data-id="element-13601" data-mp="1c4errk4l0zlo">{verb}</strong> →{' '}
                  <strong data-id="element-13602" data-mp="1c56ncuy6ixby">{stem}</strong>
                </p>
              </div>
            </div>

            {/* Step 2: Last Letter Check */}
            <div className="flex items-start gap-3" data-id="element-13603" data-mp="3a1eu0lkuiuvy">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-13604" data-mp="3a0myfar90x5o">
                <span className="text-cyan-600 font-bold text-xs" data-id="element-13605" data-mp="3nu4g0q0djzt2">2</span>
              </div>
              <div data-id="element-13606" data-mp="3ivjfeph7ne43">
                <h4 className="text-sm font-bold text-cyan-900 mb-1" data-id="element-13607" data-mp="2v3cnq3mfjvb0">
                  Kijk naar de laatste letter
                </h4>
                <p className="text-sm text-slate-600" data-id="element-13608" data-mp="2r3cb8oiw5u6l">
                  De laatste letter van de stam is de{' '}
                  <strong data-id="element-13609" data-mp="353day0nf6vkh">{lastLetter.toUpperCase()}</strong>.
                </p>
              </div>
            </div>

            {/* Step 3: KOFSCHIP Check */}
            <div className="flex items-start gap-3" data-id="element-13610" data-mp="kb1o5mvwys91">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-13611" data-mp="kdnyfua6vluy">
                <span className="text-indigo-600 font-bold text-xs" data-id="element-13612" data-mp="1xa3aragle0b0">3</span>
              </div>
              <div className="w-full" data-id="element-13613" data-mp="2296fsuwko6iz">
                <h4 className="text-sm font-bold text-indigo-900 mb-3" data-id="element-13614" data-mp="22a954ye8y0ws">
                  Zit deze in 't KOFSCHIP?
                </h4>

                <div className="flex justify-start gap-1.5 mb-3 flex-wrap" data-id="element-13615" data-mp="1obhvppabx3ez">
                  <div className="text-lg font-black text-slate-400 mr-1" data-id="element-13616" data-mp="1t9b3gmra65hd">
                    't
                  </div>
                  {kofschipLetters.map(letter => {
                  const isMatch = letter === lastLetter.toUpperCase();
                  return <div key={letter} className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-sm shadow-sm
                          ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-13617" data-mp="278j1vdfmd9ki">
                        
                        {letter}
                      </div>;
                })}
                </div>

                {!isInKofschip && <div className="text-sm text-rose-600 font-semibold mb-2" data-id="element-13618" data-mp="kgivccxjcq6x">
                    De {lastLetter.toUpperCase()} zit er NIET in.
                  </div>}
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>;
}